import { useState, useEffect, useCallback } from "react";
import { isSyncEnabled } from "./useProgressSync";

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const TOKEN_KEY = 'fruitCrush_token';

// ============== TIERED XP SYSTEM ==============
// Tiers: Easy (1-10), Normal (11-20), A Bit Hard (21-30), Harder (31-40), Super Hard (41-50)
const MAX_LEVEL = 50;
const MAX_PRESTIGE = 5;
const PRESTIGE_MASTER = 6; // Master Prestige level
const PRESTIGE_MASTER_MAX_LEVEL = 100; // Master Prestige goes up to level 100
const PRESTIGE_XP_COST = 100000; // 100,000 XP to enter next prestige

// XP requirements per tier with gradual scaling within each tier (TRIPLED for harder balance)
const XP_TIERS = {
  // Tier 1: Easy Start (Levels 1-10) - 6,000 to 7,500 XP per level
  easy: { start: 1, end: 10, baseXP: 6000, increment: 150 },
  // Tier 2: Normal (Levels 11-20) - 10,000 to 14,000 XP per level
  normal: { start: 11, end: 20, baseXP: 10000, increment: 400 },
  // Tier 3: A Bit Hard (Levels 21-30) - 18,000 to 26,000 XP per level
  hard: { start: 21, end: 30, baseXP: 18000, increment: 800 },
  // Tier 4: Harder (Levels 31-40) - 30,000 to 45,000 XP per level
  harder: { start: 31, end: 40, baseXP: 30000, increment: 1500 },
  // Tier 5: Super Hard (Levels 41-50) - 60,000 to 100,000 XP per level
  superHard: { start: 41, end: 50, baseXP: 60000, increment: 4000 }
};

// Get the tier info for a given level
const getTierForLevel = (level) => {
  if (level <= 10) return XP_TIERS.easy;
  if (level <= 20) return XP_TIERS.normal;
  if (level <= 30) return XP_TIERS.hard;
  if (level <= 40) return XP_TIERS.harder;
  return XP_TIERS.superHard;
};

// Calculate XP needed for a specific level within a prestige
export const getXPForLevel = (level) => {
  if (level <= 1) return 0;
  if (level > MAX_LEVEL) return Infinity;
  
  const tier = getTierForLevel(level);
  const levelInTier = level - tier.start;
  return tier.baseXP + (levelInTier * tier.increment);
};

// Calculate total XP needed to reach a specific level within a prestige (from level 1)
export const getXPForLevelWithinPrestige = (targetLevel) => {
  if (targetLevel <= 1) return 0;
  let totalXP = 0;
  for (let i = 2; i <= Math.min(targetLevel, MAX_LEVEL); i++) {
    totalXP += getXPForLevel(i);
  }
  return totalXP;
};

// Calculate total XP needed for one complete prestige cycle (level 1 to 50)
export const getXPForFullPrestige = () => {
  return getXPForLevelWithinPrestige(MAX_LEVEL + 1); // XP to complete level 50
};

// Calculate total XP needed to reach a specific prestige and level from absolute zero
export const getTotalXPForLevel = (prestige, level) => {
  if (prestige < 0) prestige = 0;
  if (level < 1) level = 1;
  
  let totalXP = 0;
  
  // Add XP for completed prestiges
  for (let p = 0; p < prestige; p++) {
    totalXP += getXPForFullPrestige(); // XP for levels 1-50
    totalXP += PRESTIGE_XP_COST; // 100,000 XP to enter next prestige
  }
  
  // Add XP for levels within current prestige
  totalXP += getXPForLevelWithinPrestige(level);
  
  return totalXP;
};

// Calculate XP needed for next level from current prestige and level
export const getXPForNextLevel = (prestige, currentLevel) => {
  if (prestige >= MAX_PRESTIGE && currentLevel >= MAX_LEVEL) {
    return Infinity; // Max prestige and level reached
  }
  
  if (currentLevel >= MAX_LEVEL) {
    // At level 50, need prestige XP cost to go to next prestige
    return PRESTIGE_XP_COST;
  }
  
  // Use the tiered system for next level XP
  return getXPForLevel(currentLevel + 1);
};

// Calculate prestige and level from total XP
// Calculate prestige and level from total XP
export const getPrestigeAndLevelFromXP = (totalXP) => {
  let remainingXP = totalXP;
  let prestige = 0;
  
  // Calculate how many complete prestige cycles (prestige 0-5)
  const xpPerPrestigeCycle = getXPForFullPrestige() + PRESTIGE_XP_COST;
  
  while (prestige < MAX_PRESTIGE) {
    const xpForThisPrestige = getXPForFullPrestige();
    
    // Check if we have enough XP to complete this prestige
    if (remainingXP >= xpForThisPrestige) {
      remainingXP -= xpForThisPrestige;
      
      // Check if we have enough to enter next prestige
      if (remainingXP >= PRESTIGE_XP_COST) {
        remainingXP -= PRESTIGE_XP_COST;
        prestige++;
      } else {
        // At level 50, waiting for prestige unlock
        return {
          prestige,
          level: MAX_LEVEL,
          currentXP: remainingXP,
          neededXP: PRESTIGE_XP_COST,
          isWaitingForPrestige: true,
          isMasterPrestige: false,
        };
      }
    } else {
      break;
    }
  }
  
  // Check for Master Prestige (after completing prestige 5)
  if (prestige >= MAX_PRESTIGE) {
    const xpForLastPrestige = getXPForFullPrestige();
    
    // Check if player can complete prestige 5 and enter Master
    if (remainingXP >= xpForLastPrestige) {
      remainingXP -= xpForLastPrestige;
      
      if (remainingXP >= PRESTIGE_XP_COST) {
        remainingXP -= PRESTIGE_XP_COST;
        prestige = PRESTIGE_MASTER; // Prestige 6 = Master
        
        // Calculate Master level (1-100) with scaling XP
        // Formula: 50,000 + (level - 1) * 5,000
        // Level 1→2: 50K, Level 2→3: 55K, ... Level 99→100: 540K
        let masterLevel = 1;
        
        const getMasterXPForLevel = (lvl) => 50000 + ((lvl - 1) * 5000);
        
        while (masterLevel < PRESTIGE_MASTER_MAX_LEVEL) {
          const xpNeeded = getMasterXPForLevel(masterLevel);
          if (remainingXP >= xpNeeded) {
            remainingXP -= xpNeeded;
            masterLevel++;
          } else {
            break;
          }
        }
        
        const neededForNext = masterLevel >= PRESTIGE_MASTER_MAX_LEVEL ? 0 : getMasterXPForLevel(masterLevel);
        
        return {
          prestige: PRESTIGE_MASTER,
          level: masterLevel,
          currentXP: remainingXP,
          neededXP: neededForNext,
          isWaitingForPrestige: false,
          isMasterPrestige: true,
        };
      } else {
        // At prestige 5 level 50, waiting for Master unlock
        return {
          prestige: MAX_PRESTIGE,
          level: MAX_LEVEL,
          currentXP: remainingXP,
          neededXP: PRESTIGE_XP_COST,
          isWaitingForPrestige: true,
          isMasterPrestige: false,
        };
      }
    }
  }
  
  // Calculate level within current prestige (0-5)
  let level = 1;
  let xpAtCurrentLevel = 0;
  
  while (level < MAX_LEVEL) {
    const xpForNextLevel = getXPForLevel(level + 1);
    if (xpAtCurrentLevel + xpForNextLevel > remainingXP) {
      break;
    }
    xpAtCurrentLevel += xpForNextLevel;
    level++;
  }
  
  const currentXPInLevel = remainingXP - xpAtCurrentLevel;
  const neededXPForNext = level >= MAX_LEVEL 
    ? (prestige >= MAX_PRESTIGE ? 0 : PRESTIGE_XP_COST)
    : getXPForLevel(level + 1);
  
  return {
    prestige,
    level: Math.min(level, MAX_LEVEL),
    currentXP: currentXPInLevel,
    neededXP: neededXPForNext,
    isWaitingForPrestige: false,
    isMasterPrestige: false,
  };
};

// Get XP progress within current level
export const getLevelProgress = (totalXP) => {
  const info = getPrestigeAndLevelFromXP(totalXP);
  
  // Check if at max - either Master Prestige level 100 or non-master max
  const isMaxLevel = info.isMasterPrestige 
    ? info.level >= PRESTIGE_MASTER_MAX_LEVEL 
    : (info.prestige >= MAX_PRESTIGE && info.level >= MAX_LEVEL && !info.isWaitingForPrestige);
  
  let percentage;
  if (isMaxLevel) {
    percentage = 100;
  } else if (info.isWaitingForPrestige) {
    percentage = (info.currentXP / PRESTIGE_XP_COST) * 100;
  } else {
    percentage = info.neededXP > 0 ? (info.currentXP / info.neededXP) * 100 : 100;
  }
  
  return {
    prestige: info.prestige,
    level: info.level,
    currentXP: info.currentXP,
    neededXP: info.neededXP,
    percentage: Math.min(percentage, 100),
    totalXP,
    isMaxLevel,
    isWaitingForPrestige: info.isWaitingForPrestige,
    isMasterPrestige: info.isMasterPrestige || false,
  };
};

// Add XP to backend (incremental)
const addXPToBackend = async (xpAmount) => {
  const token = localStorage.getItem(TOKEN_KEY);
  if (!token) return null;

  try {
    const response = await fetch(`${API_URL}/api/progress/add-xp?xp_amount=${xpAmount}`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
    if (response.ok) {
      return await response.json();
    }
  } catch (error) {
    console.error('Failed to add XP:', error);
  }
  return null;
};

export function useXP() {
  const [totalXP, setTotalXP] = useState(() => {
    const saved = localStorage.getItem('fruitCrush_totalXP');
    return saved ? parseInt(saved, 10) : 0;
  });
  
  const [lifetimeXP, setLifetimeXP] = useState(() => {
    const saved = localStorage.getItem('fruitCrush_lifetimeXP');
    return saved ? parseInt(saved, 10) : 0;
  });

  // Force sync from server on mount - ensures we have latest data
  useEffect(() => {
    const syncFromServer = async () => {
      const token = localStorage.getItem(TOKEN_KEY);
      if (!token) return;
      
      try {
        const response = await fetch(`${API_URL}/api/progress`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          if (data.total_xp !== undefined) {
            const serverXP = data.total_xp;
            // ALWAYS use server data - it's authoritative
            setTotalXP(serverXP);
            localStorage.setItem('fruitCrush_totalXP', serverXP.toString());
            console.log('[useXP] Synced from server - totalXP:', serverXP);
          }
          // Also sync lifetime XP from server
          if (data.lifetime_xp !== undefined) {
            const serverLifetimeXP = data.lifetime_xp;
            setLifetimeXP(serverLifetimeXP);
            localStorage.setItem('fruitCrush_lifetimeXP', serverLifetimeXP.toString());
            console.log('[useXP] Synced from server - lifetimeXP:', serverLifetimeXP);
          }
        }
      } catch (error) {
        console.error('[useXP] Failed to sync from server:', error);
      }
    };
    
    // Sync immediately on mount
    syncFromServer();
  }, []); // Only run once on mount

  // Listen for external localStorage changes (from loadProgress)
  useEffect(() => {
    const handleStorageChange = () => {
      const savedXP = localStorage.getItem('fruitCrush_totalXP');
      if (savedXP) {
        const newXP = parseInt(savedXP, 10);
        if (newXP !== totalXP) {
          // This is an external update from loadProgress
          setTotalXP(newXP);
        }
      }
    };
    
    // Check for changes periodically (storage event doesn't fire for same-tab changes)
    const interval = setInterval(handleStorageChange, 300);
    
    // Also listen for storage events from other tabs
    window.addEventListener('storage', handleStorageChange);
    
    return () => {
      clearInterval(interval);
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [totalXP]);

  // We no longer need to auto-sync totalXP changes to backend
  // The addXP function now uses the incremental endpoint
  
  useEffect(() => {
    localStorage.setItem('fruitCrush_lifetimeXP', lifetimeXP.toString());
  }, [lifetimeXP]);

  // Add XP (points earned from game) - uses incremental backend endpoint
  const addXP = useCallback(async (amount) => {
    // Update local state optimistically
    setTotalXP(prev => prev + amount);
    setLifetimeXP(prev => prev + amount);
    
    // Sync incrementally to backend (this won't overwrite, just adds)
    if (isSyncEnabled()) {
      const result = await addXPToBackend(amount);
      if (result && result.total_xp !== undefined) {
        // Update local state with server's authoritative value
        setTotalXP(result.total_xp);
        localStorage.setItem('fruitCrush_totalXP', result.total_xp.toString());
      }
      // Also update lifetime XP from server response
      if (result && result.lifetime_xp !== undefined) {
        setLifetimeXP(result.lifetime_xp);
        localStorage.setItem('fruitCrush_lifetimeXP', result.lifetime_xp.toString());
      }
    } else {
      // If not logged in, just save to localStorage
      localStorage.setItem('fruitCrush_totalXP', (totalXP + amount).toString());
      localStorage.setItem('fruitCrush_lifetimeXP', (lifetimeXP + amount).toString());
    }
  }, [totalXP, lifetimeXP]);

  // Get current level info
  const levelInfo = getLevelProgress(totalXP);

  // Check if player leveled up or prestiged after adding XP
  const checkLevelUp = useCallback((xpToAdd) => {
    const currentInfo = getPrestigeAndLevelFromXP(totalXP);
    const newInfo = getPrestigeAndLevelFromXP(totalXP + xpToAdd);
    
    const leveledUp = newInfo.level > currentInfo.level || newInfo.prestige > currentInfo.prestige;
    const prestigedUp = newInfo.prestige > currentInfo.prestige;
    
    return {
      leveledUp,
      prestigedUp,
      oldLevel: currentInfo.level,
      newLevel: newInfo.level,
      oldPrestige: currentInfo.prestige,
      newPrestige: newInfo.prestige,
    };
  }, [totalXP]);

  // Reset XP (for testing)
  const resetXP = useCallback(() => {
    setTotalXP(0);
    localStorage.setItem('fruitCrush_totalXP', '0');
  }, []);
  
  // Full reset (for testing)
  const fullReset = useCallback(() => {
    setTotalXP(0);
    setLifetimeXP(0);
    localStorage.setItem('fruitCrush_totalXP', '0');
    localStorage.setItem('fruitCrush_lifetimeXP', '0');
    localStorage.setItem('fruitCrush_completedLevels', JSON.stringify([]));
    localStorage.setItem('fruitCrush_levelScores', JSON.stringify({}));
  }, []);

  return {
    totalXP,
    lifetimeXP,
    prestige: levelInfo.prestige,
    level: levelInfo.level,
    currentXP: levelInfo.currentXP,
    neededXP: levelInfo.neededXP,
    progressPercentage: levelInfo.percentage,
    isMaxLevel: levelInfo.isMaxLevel,
    isWaitingForPrestige: levelInfo.isWaitingForPrestige,
    isMasterPrestige: levelInfo.isMasterPrestige,
    addXP,
    checkLevelUp,
    resetXP,
    fullReset,
    getLevelProgress,
    // Export for other components
    getTotalXPForLevel,
    getPrestigeAndLevelFromXP,
  };
}
